import { Host, h } from "@stencil/core";
import { focusComposedPath } from "../common/helpers";
const LISTENER_CONFIG = {
    passive: true
};
const ARIA_DESCRIBED_BY = "aria-describedby";
const ARIA_LABEL = "aria-label";
const TOOLTIP = "tooltip";
let autoId = 0;
export class ChTooltip {
    constructor() {
        this.visible = false;
        this.actionElement = undefined;
        this.actionElementAccessibleName = undefined;
        this.blockAlign = "outside-end";
        this.delay = 100;
        this.inlineAlign = "center";
    }
    #tooltipId;
    #addListenersForTheActionElement = true;
    #actualActionElement;
    // Refs
    #innerActionRef;
    el;
    visibleChanged(isVisible) {
        if (isVisible) {
            this.#removeDisplayListeners();
            this.#addListenersToHidePopover();
        }
        else {
            this.#removeHideListeners();
            this.#addListenersToDisplayPopover();
        }
    }
    actionElementChanged() {
        this.#removeAllListeners();
        this.#addListenersForTheActionElement = true;
        // We have to remove the aria-describedby and aria-label first, because the
        // actionElement can transition from null to the actual parentElement,
        // which in the end is the "same action element"
        this.#removeAriaLabelInActionElement();
        this.#removeAriaDescribedByInActionElement();
    }
    actionElementAccessibleNameChanged() {
        if (this.actionElementAccessibleName) {
            this.#addAriaLabelToTheActionElement();
        }
        else {
            this.#removeAriaLabelInActionElement();
        }
    }
    #handleEnter = () => {
        this.visible = true;
    };
    #handleLeave = () => {
        const focusedWithFocusVisible = focusComposedPath()[0] === this.#getActionElement() &&
            this.#actualActionElement.matches(":focus-visible");
        // Only remove the tooltip if the action element is not focused with
        // "focus-visible". If focused, "mouseleave" should not dismiss the tooltip
        if (!focusedWithFocusVisible) {
            this.visible = false;
        }
    };
    #addListenersToDisplayPopover = () => {
        this.#actualActionElement.addEventListener("focus", this.#handleEnter, LISTENER_CONFIG);
        this.#actualActionElement.addEventListener("mouseenter", this.#handleEnter, LISTENER_CONFIG);
    };
    #addListenersToHidePopover = () => {
        this.#actualActionElement.addEventListener("focusout", this.#handleLeave, LISTENER_CONFIG);
        this.#actualActionElement.addEventListener("mouseleave", this.#handleLeave, LISTENER_CONFIG);
    };
    #removeDisplayListeners = () => {
        this.#actualActionElement?.removeEventListener("focus", this.#handleEnter);
        this.#actualActionElement?.removeEventListener("mouseenter", this.#handleEnter);
    };
    #removeHideListeners = () => {
        this.#actualActionElement?.removeEventListener("focusout", this.#handleLeave);
        this.#actualActionElement?.removeEventListener("mouseleave", this.#handleLeave);
    };
    #addAriaDescribedByToTheActionElement = () => this.#actualActionElement?.setAttribute(ARIA_DESCRIBED_BY, this.#tooltipId);
    #addAriaLabelToTheActionElement = () => {
        if (this.actionElementAccessibleName) {
            this.#actualActionElement?.setAttribute(ARIA_LABEL, this.actionElementAccessibleName);
        }
    };
    #removeAriaDescribedByInActionElement = () => this.#actualActionElement?.removeAttribute(ARIA_DESCRIBED_BY);
    #removeAriaLabelInActionElement = () => this.#actualActionElement?.removeAttribute(ARIA_LABEL);
    #getActionElement = () => {
        if (this.actionElement === null) {
            return this.el.parentElement;
        }
        return this.actionElement ?? this.#innerActionRef;
    };
    #removeAllListeners = () => {
        this.#removeDisplayListeners();
        this.#removeHideListeners();
    };
    #actionIsInsideShadow = () => this.actionElement === undefined;
    connectedCallback() {
        this.#tooltipId ??= `ch-tooltip-${autoId++}`;
    }
    componentDidRender() {
        if (this.#addListenersForTheActionElement) {
            this.#addListenersForTheActionElement = false;
            this.#actualActionElement = this.#getActionElement();
            this.#addListenersToDisplayPopover();
            this.#addAriaDescribedByToTheActionElement();
            this.#addAriaLabelToTheActionElement();
        }
    }
    disconnectedCallback() {
        this.#removeAllListeners();
        this.#removeAriaDescribedByInActionElement();
        this.#removeAriaLabelInActionElement();
    }
    render() {
        const actionInsideShadow = this.#actionIsInsideShadow();
        return (h(Host, { key: '36a30bddb8387c54bb7d5891f875d26212af390a', id: !actionInsideShadow ? this.#tooltipId : undefined, role: !actionInsideShadow ? TOOLTIP : undefined, "aria-hidden": !actionInsideShadow && !this.visible ? "true" : undefined }, actionInsideShadow && (h("button", { key: '618a217eb8bcd0908a732a684477a0d6bb2cf0af', part: "action", type: "button", ref: el => (this.#innerActionRef = el) }, h("slot", { key: 'f031224d8a3e1bac293e9d42ea0effb72b30c415', name: "action" }))), this.visible && (h("ch-popover", { key: '60ee6d207270a78f117e1609fc899f4a0756dd5a', id: actionInsideShadow ? this.#tooltipId : undefined, role: actionInsideShadow ? TOOLTIP : undefined, style: {
                "--ch-tooltip-delay": `${this.delay}ms`
            }, part: "window",
            // Don't use #actualActionElement. On the first render is not defined
            actionElement: this.#getActionElement(), blockAlign: this.blockAlign, closeOnClickOutside: true, show: true, inlineAlign: this.inlineAlign, mode: "manual",
            // We need to sync the visible state when the popover is closed by an
            // user interaction (click outside)
            onPopoverClosed: this.#handleLeave }, h("slot", { key: '1d96f7545e09c1db9d9f42450bdb21f04d91c9f4' })))));
    }
    static get is() { return "ch-tooltip"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tooltip.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tooltip.css"]
        };
    }
    static get properties() {
        return {
            "actionElement": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLButtonElement | undefined | null",
                    "resolved": "HTMLButtonElement",
                    "references": {
                        "HTMLButtonElement": {
                            "location": "global",
                            "id": "global::HTMLButtonElement"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a reference for the action that opens the tooltip.\n\nIf `undefined`, a button on the tooltip will be rendered and the slot\n\"action\" can be used to display the content of the action.\n\nIf `null`, the parentElement will be used as the action reference."
                }
            },
            "actionElementAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the actionElement. This property is necessary to provide a label when\nthe tooltip is not displayed."
                },
                "attribute": "action-element-accessible-name",
                "reflect": false
            },
            "blockAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the block alignment of the window."
                },
                "attribute": "block-align",
                "reflect": false,
                "defaultValue": "\"outside-end\""
            },
            "delay": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the delay (in ms) for the tooltip to be displayed."
                },
                "attribute": "delay",
                "reflect": false,
                "defaultValue": "100"
            },
            "inlineAlign": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChPopoverAlign",
                    "resolved": "\"center\" | \"inside-end\" | \"inside-start\" | \"outside-end\" | \"outside-start\"",
                    "references": {
                        "ChPopoverAlign": {
                            "location": "import",
                            "path": "../popover/types",
                            "id": "src/components/popover/types.ts::ChPopoverAlign"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the inline alignment of the window."
                },
                "attribute": "inline-align",
                "reflect": false,
                "defaultValue": "\"center\""
            }
        };
    }
    static get states() {
        return {
            "visible": {}
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "visible",
                "methodName": "visibleChanged"
            }, {
                "propName": "actionElement",
                "methodName": "actionElementChanged"
            }, {
                "propName": "actionElementAccessibleName",
                "methodName": "actionElementAccessibleNameChanged"
            }];
    }
}
